import { Progress, Button, dismissToast } from '@bpinternal/ui-kit-next'
import { FC, useEffect } from 'react'
import { useLocation } from 'react-use'
import { getUseBotOnboardingStore } from '~/features/bots/stores/useBotOnboardingStore'
import { Link, Spinner } from '~/elements'
import { cn } from '~/utils'

type DeployToastProps = {
  workspaceId: string
  botId: string
}

export const BotDeployToast: FC<DeployToastProps> = ({ workspaceId, botId }) => {
  const currentLocation = useLocation()

  const useBotOnboardingStore = getUseBotOnboardingStore({ botId, workspaceId })
  const deploymentProgress = useBotOnboardingStore((state) => state.deploymentProgress)
  const setDeploymentProgress = useBotOnboardingStore((state) => state.setDeploymentProgress)
  const identityStatus = useBotOnboardingStore((state) => state.identityStatus)
  const isDeploying = useBotOnboardingStore((state) => state.isDeploying)
  const continueIdentityWorkflow = useBotOnboardingStore((state) => state.continueIdentityWorkflow)

  const hasError = identityStatus === 'error'
  const isCompleted = deploymentProgress.currentStep === 'completed'
  const isAcknowledged = deploymentProgress.currentStep === 'acknowledged'

  useEffect(() => {
    const isOnOnboardingPage = currentLocation.pathname === `/workspaces/${workspaceId}/bots/${botId}/new-bot`
    if (isDeploying && !isOnOnboardingPage) {
      continueIdentityWorkflow()
    }
  }, [botId, continueIdentityWorkflow, currentLocation.pathname, isDeploying, workspaceId])

  if (isAcknowledged) {
    dismissToast(`deployment-progress-${botId}`)
    return null
  }

  const handleClose = () => {
    if (isCompleted) {
      setDeploymentProgress({
        ...deploymentProgress,
        currentStep: 'acknowledged',
      })
    } else {
      dismissToast(`deployment-progress-${botId}`)
    }
  }

  const getToastTitle = () => {
    if (isCompleted) return 'Test your bot!'
    if (hasError) return 'Deployment failed'
    return 'Deploying bot'
  }

  const title = getToastTitle()

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col justify-between gap-2">
        <div className="flex items-center justify-between">
          <div className={cn('flex items-center font-medium', { 'text-danger-600': hasError })}>
            {title} {!isCompleted && !hasError && <Spinner size="1" className="ml-2 inline-block" />}
          </div>
          {!isCompleted && !hasError && (
            <p className="text-muted-foreground">{Math.round(deploymentProgress.progress)}%</p>
          )}
        </div>

        <Progress value={hasError ? 0 : deploymentProgress.progress} className="h-2" />
      </div>

      <div className="flex flex-row items-center justify-end gap-2">
        {isCompleted ? (
          <Button>
            <Link
              className="text-neutral-50"
              to={'/workspaces/$workspaceId/bots/$botId/new-chat'}
              params={{ workspaceId, botId }}
            >
              View Bot
            </Link>
          </Button>
        ) : (
          <Button variant={'secondary'}>
            <Link
              className="text-primary"
              to={'/workspaces/$workspaceId/bots/$botId/new-bot'}
              params={{ workspaceId, botId }}
            >
              View Deployment
            </Link>
          </Button>
        )}
        {hasError ? (
          <Button onClick={handleClose}>Close</Button>
        ) : (
          <Button variant="ghost" onClick={handleClose} className="text-primary sm:hidden">
            Close
          </Button>
        )}
      </div>
    </div>
  )
}
